import React, { useState, useEffect } from 'react';
import { History, Sparkles, CheckCircle, X, TrendingUp, Calendar, DollarSign, Loader2 } from 'lucide-react';

/**
 * UseLast Claim Component - Predictive Data Entry (Day 4-6)
 * 
 * Features:
 * - Fetch user's last claim
 * - Preview modal with claim data
 * - Apply predictions to auto-fill form
 * - Manual override capability
 */

interface UseLastClaimProps {
  onApplyPredictions?: (predictions: any) => void;
  userId?: string;
  demoMode?: boolean;  // NEW: Demo mode flag
}

export default function UseLastClaim({ onApplyPredictions, userId, demoMode = false }: UseLastClaimProps) {
  const [loading, setLoading] = useState(false);
  const [hasHistory, setHasHistory] = useState(false);
  const [lastClaim, setLastClaim] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Check if user has claim history on mount
  useEffect(() => {
    checkHistory();
  }, [userId]);

  const checkHistory = async () => {
    if (!userId) return;

    // Use demo endpoint if in demo mode (no auth required)
    const endpoint = demoMode 
      ? `${BACKEND_URL}/api/v1/predictive/last-claim-demo`
      : `${BACKEND_URL}/api/v1/predictive/last-claim`;

    // Use XMLHttpRequest to bypass rrweb-recorder (same fix as Tax Parser)
    const xhr = new XMLHttpRequest();
    xhr.open('GET', endpoint, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    // TODO: Add auth token when available (not needed in demo mode)
    if (!demoMode) {
      // xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.onload = () => {
      try {
        const result = JSON.parse(xhr.responseText);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          // SUCCESS
          if (result.has_history) {
            setHasHistory(true);
            setLastClaim(result.claim);
          }
        } else {
          // ERROR or no history
          console.log('No claim history found or error:', result.message);
        }
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
      }
    };

    xhr.onerror = () => {
      console.error('Network error checking history');
    };

    xhr.send();
  };

  const handleUseLastClaim = () => {
    setLoading(true);
    setShowModal(true);

    // Use demo endpoint if in demo mode (no auth required)
    const endpoint = demoMode 
      ? `${BACKEND_URL}/api/v1/predictive/predict-demo`
      : `${BACKEND_URL}/api/v1/predictive/predict`;

    // Use XMLHttpRequest to bypass rrweb-recorder (same fix as Tax Parser)
    const xhr = new XMLHttpRequest();
    xhr.open('POST', endpoint, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    // TODO: Add auth token when available (not needed in demo mode)
    if (!demoMode) {
      // xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.onload = () => {
      try {
        const result = JSON.parse(xhr.responseText);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          // SUCCESS
          setPredictions(result.predictions);
        } else {
          // ERROR
          setErrorMessage('Failed to load predictions. Please try again.');
          setShowModal(false);
        }
      } catch (parseError) {
        setErrorMessage('Invalid response from server');
        setShowModal(false);
      } finally {
        setLoading(false);
      }
    };

    xhr.onerror = () => {
      setErrorMessage('Network error - please try again');
      setShowModal(false);
      setLoading(false);
    };

    xhr.ontimeout = () => {
      setErrorMessage('Request timeout - please try again');
      setShowModal(false);
      setLoading(false);
    };

    xhr.timeout = 30000; // 30 seconds

    // Send request body (only needed for non-demo mode, but send anyway)
    xhr.send(JSON.stringify({
      user_id: userId,
      event_type: null, // Let system detect
      apply_seasonal: true
    }));
  };

  const handleApply = async () => {
    if (!predictions) return;

    setApplying(true);

    try {
      // Notify parent component to apply predictions
      if (onApplyPredictions) {
        onApplyPredictions(predictions);
      }

      // Close modal
      setShowModal(false);
      
      // Success message
      setSuccessMessage(`Predictions applied! Approximately 80% of fields have been auto-filled based on your ${lastClaim?.created_at ? 'previous claim from ' + new Date(lastClaim.created_at).toLocaleDateString() : 'claim history'}.`);

    } catch (error) {
      console.error('Error applying predictions:', error);
      setErrorMessage('Failed to apply predictions. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  // Don't show button if no history
  if (!hasHistory) {
    return null;
  }

  return (
    <>
      {/* Use Last Claim Button */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
              <History className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg flex items-center gap-2">
                Save Time with Smart Predictions
                <Sparkles className="w-5 h-5 text-blue-600" />
              </h3>
              <p className="text-sm text-gray-700">
                Found previous claim from {lastClaim?.created_at ? new Date(lastClaim.created_at).toLocaleDateString() : 'your history'}
              </p>
            </div>
          </div>
          <button
            onClick={handleUseLastClaim}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <History className="w-5 h-5" />
                Use Last Claim
              </>
            )}
          </button>
        </div>
        <div className="mt-3 flex items-center gap-4 text-sm text-gray-600">
          <span className="flex items-center gap-1">
            <TrendingUp className="w-4 h-4 text-green-600" />
            80% Auto-fill
          </span>
          <span className="flex items-center gap-1">
            <CheckCircle className="w-4 h-4 text-green-600" />
            1 Hour Saved
          </span>
          <span className="flex items-center gap-1">
            <Sparkles className="w-4 h-4 text-blue-600" />
            AI-Powered
          </span>
        </div>
      </div>

      {/* Preview Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-600 text-white p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <History className="w-7 h-7" />
                    Predictive Data Entry
                  </h2>
                  <p className="text-blue-100 mt-1">
                    Review predictions from your last claim
                  </p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                  <p className="text-lg font-semibold">Analyzing your claim history...</p>
                  <p className="text-sm text-gray-600 mt-2">Generating smart predictions</p>
                </div>
              ) : predictions ? (
                <div className="space-y-6">
                  {/* Last Claim Info */}
                  {lastClaim && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Calendar className="w-5 h-5 text-blue-600" />
                        <h3 className="font-semibold text-blue-900">Previous Claim</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Date</p>
                          <p className="font-semibold">{new Date(lastClaim.created_at).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Client</p>
                          <p className="font-semibold">{lastClaim.client_name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Event Type</p>
                          <p className="font-semibold">{lastClaim.event_type || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Total Claim</p>
                          <p className="font-semibold">${lastClaim.total_claim?.toLocaleString() || '0'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Confidence Score */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-green-900 mb-1">Prediction Confidence</h3>
                        <p className="text-sm text-gray-700">
                          Based on {predictions.claim_count} previous claim(s)
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-4xl font-bold text-green-700">
                          {((predictions.confidence || 0.85) * 100).toFixed(0)}%
                        </p>
                        <p className="text-xs text-green-600">High Confidence</p>
                      </div>
                    </div>
                  </div>

                  {/* Predicted Values Preview */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-blue-600" />
                      What Will Be Auto-Filled
                    </h3>
                    <div className="space-y-3">
                      {/* Revenue */}
                      {predictions.predictions?.revenue && (
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900">Revenue Data</p>
                              <p className="text-xs text-gray-600 mt-1">
                                {predictions.predictions.revenue.data?.length || 0} entries
                              </p>
                            </div>
                            <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded">
                              {((predictions.predictions.revenue.confidence || 0.9) * 100).toFixed(0)}% Confidence
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Expenses */}
                      {predictions.predictions?.expenses && (
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900">Expense Categories</p>
                              <p className="text-xs text-gray-600 mt-1">
                                {Object.keys(predictions.predictions.expenses.data || {}).length} categories
                              </p>
                            </div>
                            <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded">
                              {((predictions.predictions.expenses.confidence || 0.88) * 100).toFixed(0)}% Confidence
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Client Info */}
                      {predictions.predictions?.client_info && (
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900">Client Information</p>
                              <p className="text-xs text-gray-600 mt-1">
                                Name, business type, contact details
                              </p>
                            </div>
                            <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded">
                              {((predictions.predictions.client_info.confidence || 0.95) * 100).toFixed(0)}% Confidence
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Value Prop */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <DollarSign className="w-6 h-6 text-blue-600 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-blue-900 mb-1">
                          Save Approximately 1 Hour
                        </p>
                        <p className="text-sm text-gray-700">
                          Our AI will pre-fill about 80% of your form fields based on your previous claim. 
                          You can review and adjust any values before submitting.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-red-600">Failed to load predictions</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            {!loading && predictions && (
              <div className="border-t border-gray-200 p-6 flex justify-between items-center bg-gray-50 rounded-b-xl">
                <div>
                  <p className="text-sm text-gray-600">
                    ⚠️ Review all auto-filled data before finalizing
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApply}
                    disabled={applying}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-2 rounded-lg transition flex items-center gap-2 disabled:opacity-50"
                  >
                    {applying ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Applying...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        Apply Predictions
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
